import { z } from "zod"
import { ENVIRONMENTS, dateRangeSchema } from "./filters.js"
import { METRIC_DIMS, metricIdSchema } from "./metric-point.js"

export const WIDGET_TYPES = [
  "stat-tile",
  "bar",
  "hbar",
  "stacked-bar",
  "line",
  "pie",
  "table",
  "histogram",
  "gauge",
  "map",
] as const
export type WidgetType = (typeof WIDGET_TYPES)[number]

export const SPLIT_KEYS = ["period", "tenant", ...METRIC_DIMS] as const
export type SplitKey = (typeof SPLIT_KEYS)[number]

export const BREAKPOINTS = ["lg", "md", "sm"] as const
export type Breakpoint = (typeof BREAKPOINTS)[number]

export const tileBoxSchema = z.object({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().min(1),
  h: z.number().int().min(2),
})
export type TileBox = z.infer<typeof tileBoxSchema>

export const tileLayoutSchema = z.partialRecord(z.enum(BREAKPOINTS), tileBoxSchema)
export type TileLayout = z.infer<typeof tileLayoutSchema>

export const tileDisplaySchema = z.object({
  showLegend: z.boolean().default(true),
  legendPosition: z.enum(["bottom", "right"]).default("bottom"),
  stacked: z.boolean().default(false),
  showDataLabels: z.enum(["none", "endpoints", "extremes", "all"]).default("endpoints"),
  valueFormat: z.enum(["auto", "compact", "full", "percent", "duration"]).default("auto"),
  donut: z.boolean().default(true),
  area: z.boolean().default(false),
  sort: z.enum(["none", "asc", "desc"]).default("desc"),
})
export type TileDisplay = z.infer<typeof tileDisplaySchema>

export const tileConfigSchema = z.object({
  /** Same domain, at most 8. Split across domains into separate tiles — Cube cannot take measures from two cubes in one query */
  metrics: z.array(metricIdSchema).min(1).max(8),
  widgetType: z.enum(WIDGET_TYPES),
  splitBy: z.array(z.enum(SPLIT_KEYS)).default(["period"]),
  topN: z.number().int().min(1).max(200).optional(),
  comparePrevious: z.boolean().default(false),
  overrideFilters: z
    .object({
      tenantIds: z.array(z.string()).optional(),
      environments: z.array(z.enum(ENVIRONMENTS)).optional(),
      range: dateRangeSchema.optional(),
    })
    .nullable()
    .default(null),
  display: tileDisplaySchema.prefault({}),
})
export type TileConfig = z.infer<typeof tileConfigSchema>

export const DASHBOARD_KINDS = ["template", "personal", "shared"] as const
export type DashboardKind = (typeof DASHBOARD_KINDS)[number]

export const tileItemSchema = z.object({
  id: z.uuid(),
  dashboardId: z.uuid(),
  title: z.string().nullable(),
  widgetType: z.enum(WIDGET_TYPES),
  layout: tileLayoutSchema,
  config: tileConfigSchema,
  sortOrder: z.number().int(),
})
export type TileItem = z.infer<typeof tileItemSchema>

export const dashboardSummarySchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(DASHBOARD_KINDS),
  ownerStaffId: z.string().nullable(),
  tileCount: z.number().int(),
  isHome: z.boolean(),
  updatedAt: z.iso.datetime({ offset: true }),
})
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>

export const dashboardDetailSchema = dashboardSummarySchema.extend({
  defaultFilters: z.record(z.string(), z.unknown()).default({}),
  tiles: z.array(tileItemSchema),
})
export type DashboardDetail = z.infer<typeof dashboardDetailSchema>

export const layoutPatchSchema = z.object({
  breakpoint: z.enum(BREAKPOINTS),
  items: z
    .array(tileBoxSchema.extend({ tileId: z.uuid() }))
    .min(1)
    .max(300),
})
export type LayoutPatch = z.infer<typeof layoutPatchSchema>

export const createDashboardSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).nullable().default(null),
  copyFrom: z.string().min(1).optional(),
})
export type CreateDashboardRequest = z.infer<typeof createDashboardSchema>

export const updateDashboardSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    isHome: z.boolean().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: "Nothing to update." })
export type UpdateDashboardRequest = z.infer<typeof updateDashboardSchema>

export const createTileSchema = z.object({
  title: z.string().trim().min(1).max(120).nullable().default(null),
  config: tileConfigSchema,
  layout: tileLayoutSchema.optional(),
})
export type CreateTileRequest = z.infer<typeof createTileSchema>

export const updateTileSchema = z
  .object({
    title: z.string().trim().min(1).max(120).nullable().optional(),
    config: tileConfigSchema.optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: "Nothing to update." })
export type UpdateTileRequest = z.infer<typeof updateTileSchema>

export const dashboardContentSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).nullable(),
  tiles: z
    .array(
      z.object({
        /** Omit to create. If present it must belong to this dashboard, or the whole batch is a 400 */
        id: z.uuid().optional(),
        title: z.string().trim().min(1).max(120).nullable().default(null),
        config: tileConfigSchema,
        layout: tileLayoutSchema,
      }),
    )
    .max(300),
})
export type DashboardContent = z.infer<typeof dashboardContentSchema>
